import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  TrendingUp, 
  Users, 
  Cpu, 
  Target,
  Building2,
  Award,
  Shield,
  Lightbulb
} from 'lucide-react';
import { 
  founderInfo,
  strategicExpertise,
  leadershipExperience,
  experienceSpans,
  governanceAreas,
  visionStatement,
  visionPillars,
  philosophy
} from '../mockPages';

const AboutFounder = () => {
  const expertiseIcons = [TrendingUp, Users, Cpu, Target];

  return (
    <div className="min-h-screen" style={{backgroundColor: '#fffaf1'}}>
      <Helmet>
        <title>About N. Prabhakar Naidu - Founder of EduSocial</title>
        <meta name="description" content="Meet N. Prabhakar Naidu, IICA-Certified Independent Director with 35+ years of leadership experience in education, human capital development, and institutional transformation." />
        <meta property="og:title" content="About N. Prabhakar Naidu - Founder of EduSocial" />
        <meta property="og:description" content="Education sector advisor with decades of experience in institutional scaling and academia-industry alignment." />
        <link rel="canonical" href="https://edusocial.co.in/about-founder" />
      </Helmet>
      
      {/* Hero/Intro Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" style={{background: 'linear-gradient(135deg, #fffaf1 0%, #fff5e1 100%)'}}>
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 space-y-6">
              <div className="space-y-2">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold" style={{color: '#012726'}}>
                  {founderInfo.name}
                </h1>
                <p className="text-xl sm:text-2xl font-semibold" style={{color: '#33c375'}}>
                  {founderInfo.title}
                </p>
              </div>
              <div className="flex gap-4">
                <Badge className="px-4 py-2" style={{backgroundColor: '#e8f5ee', color: '#012726', borderColor: '#33c375'}}>
                  {founderInfo.experience}
                </Badge>
                <Badge className="px-4 py-2" style={{backgroundColor: '#e8f5ee', color: '#012726', borderColor: '#33c375'}}>
                  {founderInfo.designation}
                </Badge>
              </div>
              <p className="text-lg text-gray-600 leading-relaxed">
                {founderInfo.bio}
              </p>
            </div>
            <div className="order-1 lg:order-2">
              <img 
                src={founderInfo.photo} 
                alt={founderInfo.name} 
                className="rounded-2xl shadow-2xl w-full h-auto max-w-md mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Strategic Expertise */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Strategic Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {strategicExpertise.map((expertise, index) => {
              const Icon = expertiseIcons[index];
              return (
                <Card 
                  key={expertise.id} 
                  className="border-2 hover:shadow-lg transition-all duration-300 bg-white"
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = '#33c375'}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = ''}
                >
                  <CardContent className="p-6 space-y-4 text-center">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto"
                      style={{backgroundColor: '#33c375'}}
                    >
                      <Icon className="text-white" size={28} />
                    </div>
                    <h3 className="text-xl font-semibold" style={{color: '#012726'}}>
                      {expertise.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {expertise.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Leadership Experience */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Leadership & Institutional Experience
            </h2>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            {leadershipExperience.map((exp, index) => (
              <Card 
                key={exp.id} 
                className="border-2 bg-white hover:shadow-lg transition-all duration-300"
                style={{borderColor: index === 0 ? '#33c375' : '#e5e7eb'}}
              >
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                    <div>
                      <h3 className="text-2xl font-bold mb-1" style={{color: '#012726'}}>
                        {exp.company}
                      </h3>
                      <p className="text-lg font-semibold" style={{color: '#33c375'}}>
                        {exp.role}
                      </p>
                    </div>
                    <Badge className="px-4 py-2 mt-2 md:mt-0" style={{backgroundColor: '#e8f5ee', color: '#012726'}}>
                      {exp.duration}
                    </Badge>
                  </div>
                  <ul className="space-y-2">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx} className="flex items-start space-x-2 text-gray-700">
                        <span style={{color: '#33c375'}}>•</span>
                        <span>{achievement}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Spans */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Experience Spans
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {experienceSpans.map((span, index) => (
              <div 
                key={index} 
                className="flex items-start space-x-3 p-4 bg-white border-2 border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#33c375'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
              >
                <Award style={{color: '#33c375', flexShrink: 0}} size={20} />
                <span className="text-gray-700">{span}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Governance & Advisory */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{color: '#012726'}}>
              Governance & Advisory Role
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Contributes at the board and leadership level with deep expertise in strategic governance 
              and institutional oversight, balancing innovation with accountability for responsible, 
              structured, and sustainable growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            {governanceAreas.map((area, index) => (
              <div 
                key={index} 
                className="flex items-center space-x-3 p-6 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{backgroundColor: '#33c375'}}
                >
                  <Shield className="text-white" size={20} />
                </div>
                <span className="text-gray-700 font-medium">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision for EduSocial */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{color: '#012726'}}>
              Vision for EduSocial
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              {visionStatement}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {visionPillars.map((pillar, index) => (
              <Card 
                key={index} 
                className="border-2 bg-white hover:shadow-lg transition-all duration-300"
                style={{borderColor: '#e8f5ee'}}
              >
                <CardContent className="p-6 text-center">
                  <div 
                    className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{backgroundColor: '#33c375'}}
                  >
                    <Lightbulb className="text-white" size={24} />
                  </div>
                  <p className="text-gray-700 font-medium">{pillar}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" style={{backgroundColor: '#fffaf1'}}>
        <div className="container mx-auto max-w-3xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-8" style={{color: '#012726'}}>
            The Philosophy
          </h2>
          <div className="space-y-4">
            {philosophy.map((item, index) => (
              <p key={index} className="text-2xl font-semibold" style={{color: '#33c375'}}>
                {item}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 text-white" style={{background: 'linear-gradient(135deg, #012726 0%, #014d49 100%)'}}>
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Join the EduSocial Movement
          </h2>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto mb-8">
            Be part of transforming education through industry-integrated learning ecosystems
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="text-white px-8 py-6 text-lg transition-all duration-200 hover:shadow-lg"
              style={{backgroundColor: '#33c375'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2ab066'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#33c375'}
            >
              Explore EduSocial
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-2 text-white px-8 py-6 text-lg transition-all duration-200"
              style={{borderColor: 'white'}}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutFounder;
